import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { alpha, styled, useTheme } from '@mui/material'
import { RawImageMessage } from '@weblite/ts-action/message'
import { ActionHandler } from '$common/action-handler'
import { useContentFileStatus } from '$common/react-hooks/message'
import { showMediaViewerModal } from '$comp/modals/media-viewer'
import { getUploadingFile } from '$logic/persist-uploads'
import { useAsyncFunction } from '$helper/functions/async-function.react-hook'
import { useFileObjectUrl } from '$helper/functions/file'

const IMAGE_MAX_WIDTH = 280

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  overflow: hidden;
`

const ImageThumbnail = styled('img')`
  min-width: 80px;
  min-height: 80px;
  max-height: 500px;

  object-fit: cover;
  &[data-apply-filter='true'] {
    filter: blur(10px);
  }
  transform: scale(1.2);
`

const MainImage = styled('img')`
  min-width: 80px;
  min-height: 80px;
  max-height: 500px;

  object-fit: contain;
  z-index: 1;
  -webkit-tap-highlight-color: transparent; // TODO: Check - this is Experimental
`

const ImageBackdrop = styled('img')`
  width: 100%;
  height: 100%;
  position: absolute;

  object-fit: cover;
  filter: blur(5px);
  transform: scale(1.2);
`

export function Image({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  roomId,
  iid,
  id,
}: {
  attachment: RawImageMessage['attachment']
  statusComp: JSX.Element | null
  uploadId: string
  rootStyle?: CSSProperties
  roomId: string
  iid: number
  id?: string
}) {
  const { fileId, base64, width, height } = attachment
  const { state, percentage, blobURL, cancelOperation, startDownload } =
    useContentFileStatus({
      fileId,
      uploadId,
      messageType: 'IMAGE',
      messageId: id,
    })

  const { value: uploadingImageBlob } = useAsyncFunction(async () => {
    if (state !== 'UPLOADING') return null
    return (await getUploadingFile(uploadId))?.file
  }, [state])

  const uploadingImageBlobUrl = useFileObjectUrl(uploadingImageBlob)

  const theme = useTheme()

  const calculatedWidth = Math.min(width, IMAGE_MAX_WIDTH)
  const calculatedHeight = (calculatedWidth / width) * height

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerMinHeight, setContainerMinHeight] = useState(0)
  const [containerMinWidth, setContainerMinWidth] = useState(0)

  useEffect(() => {
    if (state !== 'AVAILABLE') {
      setContainerMinHeight(containerRef.current?.clientHeight || 0)
      setContainerMinWidth(containerRef.current?.clientWidth || 0)
    }
  }, [state])

  const openImage = useCallback(async () => {
    if (!iid) return // "Note: The iid property is initially undefined during upload but is assigned a value upon completion of the upload process."
    showMediaViewerModal({ roomId, iid })
  }, [roomId, iid])

  return (
    <>
      <Container
        key={`${width}-${height}`}
        ref={containerRef}
        style={{
          minWidth: containerMinWidth,
          minHeight: containerMinHeight,
          ...rootStyle,
        }}
      >
        {state !== 'AVAILABLE' && (
          <ActionHandler
            size={50}
            state={state}
            percentage={percentage}
            onCancelClick={cancelOperation}
            onDownloadClick={startDownload}
            rootSx={{
              position: 'absolute',
              /* @noflip */
              left: 'calc(50% - 25px)',
              top: 'calc(50% - 25px)',
              zIndex: 1,
            }}
            sx={{
              backgroundColor: alpha(theme.palette.neutral1000, 0.6),
            }}
          />
        )}

        {blobURL ? (
          <>
            <MainImage
              src={blobURL}
              alt="image-message"
              onClick={openImage}
              style={{
                width: `${calculatedWidth}px`,
                height: `${calculatedHeight}px`,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
            />
            <ImageBackdrop src={base64} alt="image-thumbnail" />
          </>
        ) : (
          <ImageThumbnail
            onClick={openImage}
            data-apply-filter={!uploadingImageBlobUrl}
            src={uploadingImageBlobUrl || base64}
            alt="image-thumbnail"
            style={{
              width: `${calculatedWidth}px`,
              height: `${calculatedHeight}px`,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          />
        )}
      </Container>

      {statusComp ? (
        <div
          style={{
            padding: '7px 10px',
          }}
        >
          {statusComp}
        </div>
      ) : null}
    </>
  )
}

Image.defaultProps = {
  rootStyle: null,
}
